/* Read-only mirror of the existing PDP overview; never calculates or submits an order. */
(() => {
  'use strict';
  if (customElements.get('ss-mobile-order-summary')) return;

  const blockers = [
    'dialog[open]', '[aria-modal="true"]', '[role="dialog"]',
    '.fancybox__container', '.fancybox-container',
    '#shopify-pc__banner', '#shopify-pc__prefs__dialog',
    '.title-text.is-tooltip-open', '.ss-19mw1016-finishing__tooltip'
  ].join(',');

  const isVisible = element => {
    if (element.hidden || element.closest('[hidden], [aria-hidden="true"]')) return false;
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && element.getClientRects().length > 0;
  };

  class MobileOrderSummary extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.main-product')?.querySelector('.product-configure');
      this.overview = this.root?.querySelector('.configure-wrapper__overview');
      this.quantity = this.overview?.querySelector('[data-config-quantity]');
      this.total = this.overview?.querySelector('[data-config-total-price]');
      if (!this.quantity || !this.total || this.controller) return;

      this.controller = new AbortController();
      const { signal } = this.controller;
      this.mobile = matchMedia('(max-width: 767px)');
      this.quantityOutput = this.querySelector('[data-summary-quantity]');
      this.totalOutput = this.querySelector('[data-summary-total]');
      this.overview.id ||= this.dataset.overviewId;
      this.querySelector('[data-summary-jump]').setAttribute('aria-controls', this.overview.id);
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };

      for (const type of ['scroll', 'resize']) window.addEventListener(type, this.schedule, { passive: true, signal });
      this.mobile.addEventListener('change', this.schedule, { signal });
      for (const type of ['input', 'change', 'click', 'focusin', 'focusout', 'close', 'toggle']) {
        document.addEventListener(type, this.schedule, { capture: true, signal });
      }
      window.visualViewport?.addEventListener('resize', this.schedule, { passive: true, signal });

      this.priceObserver = new MutationObserver(this.schedule);
      this.priceObserver.observe(this.overview, { childList: true, characterData: true, subtree: true });
      // Late-loaded consent/lightbox UI can open without another user event.
      this.uiObserver = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.schedule();
      });
      this.uiObserver.observe(document.body, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['open', 'hidden', 'aria-hidden', 'class', 'style']
      });

      this.querySelector('[data-summary-jump]').addEventListener('click', () => {
        this.hide();
        this.overview.setAttribute('tabindex', '-1');
        this.overview.focus({ preventScroll: true });
        const header = document.querySelector('header');
        const headerStyle = header && getComputedStyle(header);
        const offset = headerStyle && ['fixed', 'sticky'].includes(headerStyle.position)
          ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        window.scrollTo({
          top: Math.max(0, window.scrollY + this.overview.getBoundingClientRect().top - offset - 16),
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      }, { signal });
      this.schedule();
    }

    hide() {
      clearTimeout(this.revealTimer);
      this.revealTimer = 0;
      this.hidden = true;
    }

    update(allowReveal = false) {
      if (!this.mobile.matches) { this.hide(); return; }
      const height = window.innerHeight;
      // Shopify's preview toolbar sits in the top layer and covers fixed theme UI.
      // Use its visible wrapper: the iframe itself can be a full viewport tall.
      const preview = document.getElementById('PBarNextFrameWrapper');
      const previewRect = preview?.getBoundingClientRect();
      const previewVisible = preview && isVisible(preview) && previewRect.bottom > 0 && previewRect.top < height;
      const clearance = previewVisible ? Math.max(0, Math.ceil(height - previewRect.top)) : 0;
      const bottom = Math.max(0, height - clearance);
      const offset = `${clearance}px`;
      if (this.style.getPropertyValue('--ss-preview-clearance') !== offset) {
        this.style.setProperty('--ss-preview-clearance', offset);
      }
      const quantity = this.quantity.textContent.trim();
      const total = this.total.textContent.trim();
      const configRect = this.root.getBoundingClientRect();
      const overviewRect = this.overview.getBoundingClientRect();
      const active = document.activeElement;
      // Completed radio/select choices can retain focus while the customer scrolls.
      const editing = active?.matches('textarea, [contenteditable="true"]') ||
        (active?.tagName === 'INPUT' && ['text', 'search', 'tel', 'url', 'email', 'password', 'number'].includes(active.type));
      const keyboard = window.visualViewport && window.visualViewport.height < height * 0.75;
      const inConfiguration = configRect.top < height * 0.6 && configRect.bottom > height;
      const upload = this.root.querySelector('.configure-wrapper__design');
      const uploadRect = upload?.getBoundingClientRect();
      // Only the choices before the upload/overview count towards useful scroll length.
      // A tall upload area must not turn a short configurator into a long one.
      const choicesEnd = upload && isVisible(upload) ? Math.min(uploadRect.top, overviewRect.top) : overviewRect.top;
      const longConfiguration = choicesEnd - configRect.top >= height * 2;
      // Starting near the end would only flash the bar briefly. Once shown, keep it
      // until the upload/overview enters view; do not reappear after the upload.
      const choicesAhead = choicesEnd > bottom + (this.hidden ? height * 0.75 : 0);
      const blocked = [...document.querySelectorAll(blockers)].some(isVisible);
      const scrollLocked = getComputedStyle(document.body).overflowY === 'hidden';
      const show = bottom >= 400 && quantity && total && active !== this.overview && !editing && !keyboard &&
        longConfiguration && inConfiguration && choicesAhead && !blocked && !scrollLocked;

      if (this.quantityOutput.textContent !== quantity) this.quantityOutput.textContent = quantity;
      if (this.totalOutput.textContent !== total) this.totalOutput.textContent = total;
      if (!show) { this.hide(); return; }
      if (this.hidden && !allowReveal) {
        // Recheck fresh geometry after a short pass through the page while scrolling.
        if (!this.revealTimer) this.revealTimer = setTimeout(() => {
          this.revealTimer = 0;
          this.update(true);
        }, 200);
        return;
      }
      this.hidden = false;
    }

    disconnectedCallback() {
      this.hide();
      this.controller?.abort();
      this.controller = null;
      this.priceObserver?.disconnect();
      this.uiObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  }
  customElements.define('ss-mobile-order-summary', MobileOrderSummary);
})();
